import { FormEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { redeemVoucher } from '../api/voucherClient';
import type { VoucherRedeemAdaptation } from '../../../shared/api/intelliSightAdapters';
import { PURCHASE_VOUCHER_URL } from '../../../shared/config/externalLinks';
import {
  useAuthStore,
  selectAuthStatus,
  selectAuthError,
  selectAuthDraft,
  type AuthMode
} from '../state/authStore';

interface VoucherRedeemFormProps {
  onModeChange?: (mode: AuthMode) => void;
  variant?: 'guest' | 'authenticated';
  onSuccess?: (result: VoucherRedeemAdaptation) => void;
}

const VOUCHER_PATTERN = /^[A-Za-z0-9]+$/;

export function VoucherRedeemForm({
  onModeChange,
  variant = 'guest',
  onSuccess
}: VoucherRedeemFormProps) {
  const { t } = useTranslation();
  const { setAuthenticating, setAuthenticated, setError } = useAuthStore();
  const status = useAuthStore(selectAuthStatus);
  const error = useAuthStore(selectAuthError);
  const draft = useAuthStore(selectAuthDraft);
  const { voucherCode, hasAgreed } = draft;
  const updateDraft = useAuthStore((state) => state.updateDraft);

  const [localError, setLocalError] = useState<string | null>(null);
  const voucherInputId = useId();
  const requiresPolicyConsent = variant === 'guest';
  const requiresIntentSelection = variant === 'guest';
  const [guestAcknowledged, setGuestAcknowledged] = useState(variant !== 'guest');
  useEffect(() => {
    setGuestAcknowledged(variant !== 'guest');
  }, [variant]);
  const titleKey =
    variant === 'guest' ? 'voucherActivation.title' : 'voucherActivation.authenticatedTitle';
  const subtitleKey =
    variant === 'guest' ? 'voucherActivation.subtitle' : 'voucherActivation.authenticatedSubtitle';
  const helperKey =
    variant === 'guest' ? 'voucherActivation.helper' : 'voucherActivation.authenticatedHelper';

  useEffect(() => {
    setError(null);
    setLocalError(null);
  }, [setError]);

  const isSubmitting = status === 'authenticating';
  const feedbackError = localError ?? error;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const rawValue = voucherCode;
    const trimmedValue = rawValue.trim();

    setLocalError(null);
    setError(null);

    if (!trimmedValue) {
      setLocalError(t('voucherActivation.errors.missingCode'));
      return;
    }
    if (!VOUCHER_PATTERN.test(rawValue)) {
      setLocalError(t('voucherActivation.errors.invalidFormat'));
      return;
    }
    if (requiresPolicyConsent && !hasAgreed) {
      setLocalError(t('account.errors.acceptPolicies'));
      return;
    }
    if (requiresIntentSelection && !guestAcknowledged) {
      setLocalError(t('voucherActivation.errors.missingChoice'));
      return;
    }

    setAuthenticating();

    try {
      const result = await redeemVoucher({ voucherCode: rawValue });
      const derivedName =
        result.alias?.trim().length ? result.alias.trim() : result.email.split('@')[0];
      setAuthenticated(result.email, result.token, derivedName);
      onSuccess?.(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('voucherActivation.errors.generic');
      setError(message);
    }
  };

  return (
    <form className="space-y-6 sm:space-y-7" onSubmit={handleSubmit} noValidate>
      <div className="rounded-3xl bg-gradient-to-br from-ai-primary via-ai-secondary to-ai-accent p-[1px] shadow-ai-glow">
        <div className="rounded-[calc(1.5rem-1px)] bg-ai-surface-elevated/95 p-6 sm:p-8">
          {variant === 'guest' ? (
            <div className="mb-6 rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 p-4 text-left sm:p-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  className="w-full rounded-3xl bg-gradient-to-br from-ai-primary/70 via-ai-secondary/60 to-ai-accent/70 px-5 py-5 text-left text-white shadow-[0_12px_32px_-18px_rgba(255,97,56,0.65)] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                  onClick={() => onModeChange?.('login')}
                >
                  <span className="text-[0.75rem] font-semibold uppercase tracking-[0.35em]">
                    {t('voucherActivation.choice.signInTitle')}
                  </span>
                  <span className="mt-2 block text-sm text-white/85">
                    {t('voucherActivation.choice.signInDescription')}
                  </span>
                </button>
                <button
                  type="button"
                  className={`w-full rounded-3xl px-5 py-5 text-left shadow-inner transition-transform duration-200 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus ${guestAcknowledged ? 'bg-gradient-to-br from-ai-primary via-ai-secondary to-ai-accent text-white shadow-[0_18px_45px_-18px_rgba(255,97,56,0.85)] -translate-y-0.5' : 'border border-ai-surface-glow/30 bg-ai-surface-elevated/70 text-ai-text-secondary hover:-translate-y-0.5 hover:border-ai-primary/60 hover:text-ai-text-primary'}`}
                  onClick={() => setGuestAcknowledged(true)}
                  aria-pressed={guestAcknowledged}
                >
                  <span className="text-[0.75rem] font-semibold uppercase tracking-[0.35em]">
                    {t('voucherActivation.choice.guestTitle')}
                  </span>
                  <span
                    className={`mt-2 block text-sm font-semibold ${guestAcknowledged ? 'text-white/85' : 'text-ai-text-secondary'}`}
                  >
                    {t('voucherActivation.choice.guestDescription')}
                  </span>
                </button>
              </div>
            </div>
          ) : null}

          <div className="space-y-4 text-center">
            <h3 className="font-display text-2xl font-semibold text-ai-text-primary sm:text-3xl">
              {t(titleKey)}
            </h3>
            <p className="text-sm text-ai-text-secondary sm:text-base">
              {t(subtitleKey)}
            </p>
          </div>

          <div className="mt-6 space-y-3 sm:mt-7 sm:space-y-4">
            <label
              htmlFor={voucherInputId}
              className="text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-ai-text-secondary sm:text-xs"
            >
              {t('voucherActivation.codeLabel')}
            </label>
            <input
              id={voucherInputId}
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              spellCheck={false}
              value={voucherCode}
              onChange={(event) => updateDraft({ voucherCode: event.target.value })}
              className="w-full rounded-xl border border-ai-primary/40 bg-black/40 px-4 py-3 text-base font-semibold tracking-[0.3em] text-ai-text-primary shadow-inner focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:px-5 sm:py-3.5 sm:text-lg"
              placeholder={t('voucherActivation.codePlaceholder')}
              aria-describedby={`${voucherInputId}-helper`}
              disabled={isSubmitting}
              required
            />
            <p id={`${voucherInputId}-helper`} className="text-xs text-ai-text-secondary sm:text-sm">
              {t(helperKey)}
            </p>
          </div>
          {requiresPolicyConsent ? (
            <label className="mt-6 flex items-start gap-3 rounded-2xl border border-transparent bg-ai-surface-elevated/60 px-4 py-3 text-xs text-ai-text-secondary transition-colors focus-within:border-ai-primary/60 sm:mt-7 sm:text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 rounded border-ai-surface-glow/40 bg-ai-surface-elevated/90 text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                checked={hasAgreed}
                onChange={(event) => updateDraft({ hasAgreed: event.target.checked })}
                disabled={isSubmitting}
                required
              />
              <span>
                {t('account.policyPrompt')}{' '}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-ai-primary underline-offset-4 hover:underline"
                >
                  {t('account.termsLink')}
                </a>{' '}
                {t('account.policyAnd')}
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 font-semibold text-ai-primary underline-offset-4 hover:underline"
                >
                  {t('account.privacyLink')}
                </a>
              </span>
            </label>
          ) : null}

          {feedbackError ? (
            <div
              role="alert"
              className="mt-5 rounded-2xl border border-ai-danger/50 bg-ai-danger/10 px-4 py-3 text-sm text-ai-danger sm:px-5 sm:py-3.5"
            >
              {feedbackError}
            </div>
          ) : null}

          {!feedbackError && status === 'authenticating' ? (
            <div
              role="status"
              className="mt-5 rounded-2xl border border-ai-primary/40 bg-ai-primary/10 px-4 py-3 text-sm text-ai-primary sm:px-5 sm:py-3.5"
            >
              {t('voucherActivation.progress')}
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="submit"
            className="w-full rounded-full bg-gradient-to-r from-[#ff6138] via-[#ffbe53] to-[#ffd452] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-black shadow-[0_12px_32px_-12px_rgba(255,97,56,0.65)] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:opacity-65 sm:flex-1 sm:px-8 sm:py-3.5 sm:text-base"
            disabled={
              isSubmitting ||
              (requiresPolicyConsent && !hasAgreed) ||
              (requiresIntentSelection && !guestAcknowledged)
            }
          >
            {isSubmitting ? t('voucherActivation.submitting') : t('voucherActivation.submit')}
          </button>
          <a
            href={PURCHASE_VOUCHER_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-full items-center justify-center rounded-full border border-ai-primary/50 px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-ai-primary transition-colors duration-200 hover:border-ai-primary hover:bg-ai-primary/10 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:flex-1 sm:px-8 sm:py-3.5 sm:text-base"
            aria-label={t('voucherActivation.purchase')}
          >
            {t('voucherActivation.purchase')}
          </a>
        </div>
        {onModeChange ? (
          <button
            type="button"
            className="w-full rounded-full border border-ai-surface-glow/40 bg-ai-surface-elevated/70 px-6 py-2.5 text-sm font-medium text-ai-text-primary transition-colors hover:border-ai-primary/70 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:px-8 sm:py-3"
            onClick={() => onModeChange('login')}
            disabled={isSubmitting}
          >
            {t('voucherActivation.actions.back')}
          </button>
        ) : null}
      </div>
    </form>
  );
}
